import { webDownload } from "creso-core/hooks";
import type { SchemeFolioHolding } from "creso-core/types";
import { sanitizeFileName, type ZipArchive } from "../../utils/zip";

export type FamilyCobMember = {
  clientId: number;
  name: string;
  investmentAccount: string;
};

export function buildBrokerFolioMapFromHoldings(
  holdings: SchemeFolioHolding[]
): Record<string, number[]> {
  const brokerFolioMap: Record<string, number[]> = {};

  holdings.forEach((scheme) => {
    scheme.folio_data?.forEach((folio) => {
      if (folio.internal_flag || !folio.redemption_allowed) return;

      if (!brokerFolioMap[folio.broker_code]) {
        brokerFolioMap[folio.broker_code] = [];
      }

      if (!brokerFolioMap[folio.broker_code].includes(folio.folio_id)) {
        brokerFolioMap[folio.broker_code].push(folio.folio_id);
      }
    });
  });

  return brokerFolioMap;
}

export async function downloadFamilyCobZip({
  familyName,
  members,
  zip,
  createChangeOfBrokerRequest,
  getHoldings,
  generatePdf,
  onProgress,
}: {
  familyName: string;
  members: FamilyCobMember[];
  zip: ZipArchive;
  createChangeOfBrokerRequest: (payload: {
    investmentAccountId: string;
    preference: Record<string, number[]>;
  }) => Promise<unknown>;
  getHoldings: (investmentAccount: string) => Promise<SchemeFolioHolding[]>;
  /** PDF path: generate + add to zip via the hook downloader. */
  generatePdf: (clientId: number, fileName: string) => Promise<void>;
  onProgress?: (current: number, total: number) => void;
}) {
  if (!members.length) {
    throw new Error("No members with investment accounts found");
  }

  let processedCount = 0;

  await members.reduce<Promise<void>>(async (previous, member) => {
    await previous;

    const holdings = await getHoldings(member.investmentAccount);
    const preference = buildBrokerFolioMapFromHoldings(holdings);
    if (!Object.keys(preference).length) return;

    processedCount += 1;
    onProgress?.(processedCount, members.length);

    await createChangeOfBrokerRequest({
      investmentAccountId: member.investmentAccount,
      preference,
    });

    await generatePdf(
      member.clientId,
      `${sanitizeFileName(member.name)}-change-of-broker.pdf`
    );
  }, Promise.resolve());

  if (!Object.keys(zip.files).length) {
    throw new Error("No folios available for change of broker");
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  await webDownload(
    zipBlob,
    `${sanitizeFileName(familyName)}-change-of-broker.zip`
  );
}
